import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { UserDataContext } from 'context';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import FooterTailwind from 'TailwindcssComponents/Footer';
import { Icon } from "@iconify/react";
import defaultLogo from 'assets/images/logos/defaultlogo.png';
import defaultcover from 'assets/images/logos/defaultcover.png'
import 'react-toastify/dist/ReactToastify.css';

export default function SingleEventOrganization() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const bearerToken = localStorage.getItem('token');
  const { userData, selectedOrganization } = useContext(UserDataContext);
  const userRole = userData?.isAdmin ? 'admin' : selectedOrganization?.name || 'user';
  const { id: paramId } = useParams();
  const id = selectedOrganization?.organization?.id || paramId || null;
  const [organizationData, setOrganizationData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEventLoading, setIsEventLoading] = useState(true);
  const [eventsData, setEventsData] = useState([]);
  const [isArchived, setIsArchived] = useState(true);
  const [initialStatus, setInitialStatus] = useState(null);
  const [rowIsArchived, setRowIsArchived] = useState(initialStatus);
  const navigate = useNavigate();
  const [previewImage, setPreviewImage] = useState(null);




  const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const previousDay = new Date();
  previousDay.setDate(previousDay.getDate() - 1);
  const endDate = previousDay.toISOString().split('T')[0]; // One day before current date

  const previousYear = new Date();
  previousYear.setFullYear(previousYear.getFullYear() - 1);
  const startDate = previousYear.toISOString().split('T')[0]; // One year before current date

  const openPreview = (imgUrl) => {
    setPreviewImage(imgUrl);
  };

  const closePreview = () => {
    setPreviewImage(null);
  };

  const fetchOrganization = async () => {
    if (!id || !bearerToken) {
      console.warn("ID or Bearer Token is missing.");
      return;
    }
    setIsLoading(true);
    try {
      const orgResponse = await axios.get(`${apiUrl}auth-service/org/${id}`, {
        headers: { Authorization: `Bearer ${bearerToken}` },
      });
      if (!orgResponse.data?.success || !orgResponse.data?.data) {
        throw new Error('Failed to fetch organization data');
      } else {
        setOrganizationData(orgResponse.data.data);
      }

    } catch (error) {
      console.error("Error fetching organization data:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchOrganization();
  }, [bearerToken, id])

  const fetchData = async () => {
    if (!id || !bearerToken) {
      console.warn("ID or Bearer Token is missing.");
      return;
    }
    setIsEventLoading(true);
    try {
      const payLoad = {
        orgId: id,
        ...(isArchived
          ? { startDate: currentDate } // present events, events from today onwards
          : { startDate, endDate }     // past events, from 1 year ago to yesterday
        )
      };

      const [eventsResponse, ticketResponse, registrationsResponse, paymentResponse, venueResponse] = await Promise.all([
        axios.get(`${apiUrl}event-service/event`, {
          params: payLoad,
        }),
        axios.get(`${apiUrl}event-service/event-ticket`
        ),
        axios.get(`${apiUrl}event-service/event-registration`
        ),
        axios.get(`${apiUrl}event-service/event-payment`, {
          params: { status: 'paid' },
        }),
        axios.get(`${apiUrl}event-service/event-venue`)
      ]);

      if (!eventsResponse.data?.success || !eventsResponse.data?.data) throw new Error('Failed to fetch events');
      if (!ticketResponse.data?.success || !ticketResponse.data?.data) throw new Error('Failed to fetch tickets');
      if (!registrationsResponse.data?.success || !registrationsResponse.data?.data) throw new Error('Failed to fetch registrations');
      if (!paymentResponse.data?.success || !paymentResponse.data?.data) throw new Error('Failed to fetch payments');
      if (!venueResponse.data?.success || !venueResponse.data?.data) throw new Error('Failed to fetch venues');

      const events = eventsResponse?.data.data;
      const tickets = ticketResponse?.data.data;
      const registrations = registrationsResponse?.data.data;
      const payments = paymentResponse?.data.data;
      const venues = venueResponse?.data.data

      const ticketsByEventId = tickets.reduce((acc, ticket) => {
        acc[ticket.eventId] = (acc[ticket.eventId] || 0) + 1;
        return acc;
      }, {});

      const registrationsByEventId = registrations.reduce((acc, registration) => {
        acc[registration.eventId] = (acc[registration.eventId] || 0) + 1;
        return acc;
      }, {});

      // Get tickets count via payments and registrations
      const ticketsByPayment = payments.reduce((acc, payment) => {
        const registrationIds = payment.registrationIds || [];
        registrationIds.forEach((regId) => {
          const ticketCount = tickets.filter(ticket => ticket.registrationId === regId).length;
          acc[payment.eventId] = (acc[payment.eventId] || 0) + ticketCount;
        });
        return acc;
      }, {});

      // Enhance events with counts
      const enrichedEvents = events.map(event => ({
        ...event,
        ticketCount: ticketsByEventId[event.id] || 0,
        registrationCount: registrationsByEventId[event.id] || 0,
        ticketCountByPayment: ticketsByPayment[event.id] || 0,
        venue: venues.find(venue => venue.id === event.venueId) || null
      }));
      setEventsData(enrichedEvents);
      // setTotalItemsCount(eventsResponse.data.totalItems || 0);
      // setTotalPagesCount(eventsResponse.data.totalPages || 0);
      // setTicketsData(tickets);
      // setTotalTicketsCount(tickets.length);
      // setRegistrationsData(registrations);

    } catch (error) {
      console.error("Error fetching data:", error.response?.data?.message || error.message);
    } finally {
      setIsEventLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [bearerToken, id, isArchived]);

  const socialMedia = organizationData?.socialMedia ? JSON.parse(organizationData.socialMedia) : {};

  const getIconName = (platform) => {
    const icons = {
      YouTube: 'mdi:youtube',
      LinkedIn: 'mdi:linkedin',
      Facebook: 'mdi:facebook',
      Twitter: 'mdi:twitter',
      Instagram: 'mdi:instagram',
      TikTok: 'ic:baseline-tiktok'
    };
    return icons[platform] || 'fluent-mdl2:website';
  };

  const handlePartners = async (id, newValue) => {
    setOrganizationData(prevData => ({
      ...prevData,
      isPartner: newValue,
    }));

    try {
      const endpoint = `${apiUrl}auth-service/org/${newValue ? 'add-partner' : 'remove-partner'}/${id}`;

      const response = await axios.patch(endpoint, null, {
        headers: { Authorization: `Bearer ${bearerToken}` },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || `Unable to ${newValue ? 'add' : 'remove'} as a partner`);
      }

      toast.success(response.data.message || `Partner Organization ${newValue ? 'added' : 'removed'} successfully`);
    } catch (error) {
      // Revert state in case of an error
      setOrganizationData(prevData => ({
        ...prevData,
        status: newValue,
      }));
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const formattedDate = (date) => {
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Date(date).toLocaleDateString('en-GB', options);
  };

  const formatTime = (timeStr) => {
    return new Date(`1970-01-01T${timeStr}`).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const toggleArchiveFilter = (status) => {
    setIsArchived(status);
  };

  const handleReset = () => {
    setIsArchived(true);
  };

  const handleEdit = () => {
    navigate(`/eventOrganizations/edit-eventOrganization/${id}`);
  };

  // Function to call backend
  const handleArchive = async (id, archive) => {
    try {
      const url = `${apiUrl}auth-service/org/${archive ? "archive" : "unarchive"}/${id}`;
      const response = await axios.patch(url, {}, {
        headers: { Authorization: `Bearer ${bearerToken}` },
      });

      if (response?.data.success) {
        toast.success(
          response.data.message || `Organization has been ${archive ? "archived" : "unarchived"}`
        );
        return true; // success
      } else {
        throw new Error(response?.data.message || "Unable to update");
      }
    } catch (error) {
      const errorMsg = error.response?.data.message || error.message;
      toast.error(errorMsg);
      console.error(errorMsg);
      return false; // failed
    }
  };

  // Function to toggle archive/unarchive
  const handleArchiveToggle = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const success = await handleArchive(id, !rowIsArchived);

      if (success) {
        setRowIsArchived(!rowIsArchived);
        navigate('/eventOrganizations');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <TailwindLayout>
      <TopBar />
      {isLoading && organizationData.length === 0 ? (
        <div className="relative mb-8 p-12 animate-pulse">
          {/* Cover Image Skeleton */}
          <div className="relative min-h-[300px] rounded-xl bg-gray-300 overflow-hidden" />

          {/* Profile Card Skeleton */}
          <div className="relative -mt-24 mx-4 bg-white rounded-xl shadow-lg p-6">
            <div className="flex flex-wrap items-center gap-6">
              {/* Profile Image Skeleton */}
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gray-300 border-4 border-white shadow-md" />
              </div>

              {/* Organization Name Skeleton */}
              <div className="flex-grow">
                <div className="h-8 bg-gray-300 rounded w-64 mb-2" />
                <div className="flex items-center gap-3 mt-1">
                  <div className="h-4 bg-gray-300 rounded w-32" />
                  <div className="w-14 h-7 bg-gray-300 rounded-full" />
                </div>
              </div>

              {/* Social Media Skeleton */}
              <div className="flex gap-4 mt-1">
                <div className="w-6 h-6 bg-gray-300 rounded" />
                <div className="w-6 h-6 bg-gray-300 rounded" />
                <div className="w-6 h-6 bg-gray-300 rounded" />
                <div className="w-6 h-6 bg-gray-300 rounded" />
              </div>
            </div>
          </div>

          {/* Information Cards Skeleton */}
          <div className='mt-8 flex flex-col lg:flex-row gap-6'>
            {/* Organization Information Card Skeleton */}
            <div className="w-full lg:w-1/3 bg-white rounded-xl shadow-sm h-full">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="h-6 bg-gray-300 rounded w-48 mb-4" />

                <div className="space-y-4">
                  {/* Organization Info Items */}
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="flex items-start gap-2">
                      <div className="w-5 h-5 bg-gray-300 rounded mt-0.5 flex-shrink-0" />
                      <div className="flex-grow">
                        <div className="h-4 bg-gray-300 rounded w-16 mb-1" />
                        <div className="h-4 bg-gray-300 rounded w-32" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Events Section Skeleton */}
            <div className='w-full lg:w-2/3 space-y-2'>
              {/* Toggle Buttons Skeleton */}
              <div className="flex justify-end mb-4">
                <div className="inline-flex border rounded-lg overflow-hidden shadow-sm">
                  <div className="w-28 h-10 bg-gray-300" />
                  <div className="w-24 h-10 bg-gray-300" />
                </div>
              </div>

              {/* Event Cards Skeleton */}
              {[1, 2, 3].map((event) => (
                <div key={event} className="bg-white rounded-lg overflow-hidden mb-2">
                  <div className="flex items-center p-4">
                    {/* Event Image Skeleton */}
                    <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-md bg-gray-300" />
                    </div>

                    {/* Event Details Skeleton */}
                    <div className="flex-grow px-4">
                      <div className="h-5 bg-gray-300 rounded w-48 mb-1" />
                      <div className="h-4 bg-gray-300 rounded w-64" />
                    </div>

                    {/* Stats Skeleton */}
                    <div className="flex-shrink-0 text-center px-4 w-40">
                      <div className="h-4 bg-gray-300 rounded w-32" />
                    </div>
                    <div className="flex-shrink-0 text-center px-4 w-40">
                      <div className="h-4 bg-gray-300 rounded w-32" />
                    </div>
                    <div className="flex-shrink-0 text-center px-4 w-40">
                      <div className="h-4 bg-gray-300 rounded w-28" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="relative mb-8 p-12">
            {/* Cover Image Section */}
            <div
              className="relative min-h-[300px] rounded-xl bg-cover bg-center overflow-hidden cursor-pointer"
              style={{
                backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.6), rgba(37, 99, 235, 0.6)), url(${organizationData.coverImage?.url || defaultcover})`,
              }}
              onClick={() => openPreview(organizationData.coverImage?.url || defaultcover)}
            />
            {/* Profile Card */}
            <div className="relative -mt-24 mx-4 bg-white rounded-xl shadow-lg p-6">
              <div className="flex flex-wrap items-center gap-6">
                {/* Profile Image */}
                <div className="relative cursor-pointer" onClick={() => openPreview(organizationData.orgImage?.url || defaultLogo)}>
                  <img
                    src={organizationData.orgImage?.url || defaultLogo}
                    alt="profile"
                    className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
                  />
                </div>
                {/* Preview Modal */}
                {previewImage && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
                    onClick={closePreview}
                  >
                    <img
                      src={previewImage}
                      alt="preview"
                      className="max-w-[90%] max-h-[90%] rounded-lg shadow-lg"
                    />
                  </div>
                )}
                {/* Organization Name */}
                <div className="flex-grow">
                  <h1 className="text-2xl font-semibold">
                    {organizationData.name || ""}
                  </h1>

                  {userRole === 'admin' && organizationData?.website && (
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-sm font-medium text-gray-900 mr-1">Featured In Home Page</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={organizationData.isPartner}
                          onChange={(e) => handlePartners(organizationData.id, e.target.checked)}
                          aria-label="Toggle partnership status"
                        />
                        <div className="w-14 h-7 bg-gray-300 peer-focus:ring-2 peer-focus:ring-blue-400 rounded-full relative transition-all peer-checked:bg-blue-600">
                          <div className={`absolute top-1 left-1 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-md transition-all transform   ${organizationData.isPartner ? "translate-x-7 border-white" : ""}`} />
                        </div>
                      </label>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">

                  {/* action buttons */}
                  {userRole === "admin" && (
                    <div className="flex justify-end gap-4 mb-2 ">
                      {/* Edit button */}
                      <button
                        onClick={handleEdit} // your edit handler here
                        disabled={userRole !== 'admin'}

                        className="transition disabled:cursor-not-allowed"
                        title="Edit"
                      >
                        <Icon
                          icon="uil:edit"
                          className="h-7 w-7 px-1 text-2xl text-[#45B369] bg-[#45B369] bg-opacity-15 rounded-full hover:bg-opacity-45 hover:text-green-700"
                        />
                      </button>

                      {/* Archive / Unarchive button */}
                      <button
                        onClick={handleArchiveToggle}
                        title={rowIsArchived ? "Unarchive" : "Archive"}
                        disabled={userRole !== "admin" || isLoading}
                        className="transition disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          // simple spinner
                          <div className="h-7 w-7 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Icon
                            icon={rowIsArchived ? "system-uicons:unarchive" : "ion:archive-outline"}
                            className="h-7 w-7 px-1 text-2xl text-[#EF4A00] bg-[#F86624] bg-opacity-15 rounded-full hover:bg-opacity-45 hover:text-orange-700"
                          />
                        )}
                      </button>



                    </div>
                  )}




                  {/* social media */}
                  <div className="flex gap-4 mt-1">
                    <span>
                      <a
                        href={organizationData.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        <Icon icon="mdi:web" width="24" height="24" />
                      </a>
                    </span>
                    {socialMedia && Object.entries(socialMedia).map(([platform, url]) => (
                      url ? (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-blue-600 transition-colors"
                        >
                          <Icon icon={getIconName(platform)} width="24" height="24" />
                        </a>
                      ) : null
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {/* tab button */}
            <div className="flex justify-end pt-6 -mb-4">
              <div className="inline-flex border rounded-lg overflow-hidden shadow-sm">
                <button
                  onClick={() => toggleArchiveFilter(true)}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${isArchived
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Present Events
                </button>
                <button
                  onClick={() => toggleArchiveFilter(false)}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${!isArchived
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Past Events
                </button>
              </div>
            </div>

            {/* Information Cards */}
            <div className='mt-8 flex flex-col lg:flex-row gap-6'>
              <div className="w-full lg:w-1/3 bg-white rounded-xl shadow-sm h-full">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-medium mb-4">
                    Organization Information
                  </h2>

                  <div className="space-y-4">
                    <div className="flex items-start gap-2">
                      <Icon icon="icons8:organization" className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm">Name</p>
                        <p className="mt-1 text-sm font-medium">
                          {organizationData.name || '-'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Icon icon="fluent:location-24-regular" className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm">Address</p>
                        <p className="mt-1 text-sm font-medium">
                          {organizationData.address || '-'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Icon icon="mdi-light:phone" className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm">Contact Number</p>
                        <p className="mt-1 text-sm font-medium">
                          {organizationData.phone || '-'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Icon icon="mdi-light:email" className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm">Contact Email</p>
                        <p className="mt-1 text-sm font-medium">
                          {organizationData.email || '-'}
                        </p>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* events */}
              <div className='w-full lg:w-2/3 space-y-2'>
                {isEventLoading ? (
                  <div className="w-full flex items-center justify-center py-8">
                    <svg
                      aria-hidden="true"
                      className="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M100 50.5908C100 78.2051 ..." fill="currentColor" />
                      <path d="M93.9676 39.0409C96.393 ..." fill="currentFill" />
                    </svg>
                    <span className="ml-2 text-gray-500">Loading events...</span>
                  </div>
                ) : (
                  <>
                    {eventsData.length <= 0 ? (
                      <div className="text-gray-500 mb-4 justify-center text-center">
                        {isArchived ? "No Present Events To Show" : "No Past Events To Show"}
                      </div>
                    ) : (
                      eventsData.map((row, index) => (
                        <div key={index} className="bg-white rounded-lg overflow-hidden mb-2">
                          <div className="flex items-center p-4">
                            <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center text-white">
                              <img src={row.coverImage?.secure_url || defaultLogo} alt={`${row.name} cover`} className="w-12 h-12 rounded-md object-cover" />
                            </div>
                            <div className="flex-grow px-4">
                              <div className="font-semibold">{row.name}</div>
                              <div className="text-sm text-gray-600">{formattedDate(row.eventDateTimes[0]?.date)} | {formatTime(row.eventDateTimes[0]?.startTime)} | {row.venue?.city || ''}</div>
                            </div>
                            <div className="flex-shrink-0 text-center px-4 w-40">
                              <div className="">Total Tickets Registered: {row.ticketCount}</div>
                            </div>
                            <div className="flex-shrink-0 text-center px-4 w-40">
                              <div className="">Total Tickets Confirmed: {row.ticketCountByPayment}</div>
                            </div>
                            <div className="flex-shrink-0 text-center px-4 w-40">
                              <div className="">Total Users Booked: {row.registrationCount}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>
            </div>

          </div>
        </>
      )}

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <FooterTailwind />
    </TailwindLayout >
  )
}